import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

import UsageMetrics from './components/UsageMetrics';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

// ============================================================================
// MODULAR PRICING MODEL
// ============================================================================
// CRM is the foundation (required). Add-ons are independently selectable.
// Bundle discount: 2 add-ons = -10%, 3 add-ons = -20%.
// Annual billing = -20% off entire subtotal.
//
// Phase 1 (NOW): UI complete, Subscribe → "early access" modal (no Stripe wire-up yet).
// Phase 2 (LATER): wire Subscribe to create-checkout-session with new modular prices.
// ============================================================================

const CRM_FOUNDATION = {
  id: 'crm',
  name: 'Wilcart CRM',
  tagline: 'The foundation',
  description: 'Leads, contacts, jobs, pipeline, deals, calendar, email integration, and reports — everything you need to run your service business.',
  price: 39,
  icon: 'LayoutDashboard',
  color: 'from-blue-500 to-blue-600',
  features: [
    'Unlimited contacts & leads',
    'Pipeline & deal management',
    'Jobs & scheduling',
    'Email integration',
    'Reports & analytics',
    'Team collaboration (up to 3)',
  ],
};

const ADDONS = [
  {
    id: 'ai_voice',
    name: 'AI Voice',
    tagline: 'Never miss a lead',
    description: 'AI answers your calls 24/7, transcribes them, runs smart scripts, analyzes sentiment.',
    price: 79,
    icon: 'PhoneCall',
    color: 'from-emerald-500 to-emerald-600',
    features: [
      'AI Voice calls & dialer',
      'Call transcripts & summaries',
      'Sentiment analysis',
      'AI call scripts',
    ],
  },
  {
    id: 'website_builder',
    name: 'Website Builder',
    tagline: 'Launch your site in a day',
    description: 'Drag-and-drop AI website editor, SEO tools, 1 free domain, managed hosting included.',
    price: 50,
    icon: 'Globe',
    color: 'from-purple-500 to-purple-600',
    features: [
      'AI Website Builder',
      'Drag & drop page editor',
      'SEO optimization tools',
      '1 domain included',
      'Managed hosting',
    ],
  },
  {
    id: 'ai_marketing',
    name: 'AI Marketing',
    tagline: 'Marketing that runs itself',
    description: 'Email & SMS campaigns, AI content generation, audience segmentation, advanced analytics.',
    price: 99,
    icon: 'Sparkles',
    color: 'from-orange-500 to-pink-500',
    features: [
      'AI email & SMS campaigns',
      'AI content generation',
      'Audience segmentation',
      'Advanced marketing analytics',
    ],
  },
];

// Bundle discount: 2 add-ons → 10% off, 3 add-ons → 20% off (applies to subtotal)
const BUNDLE_DISCOUNTS = { 0: 0, 1: 0, 2: 0.10, 3: 0.20 };
const ANNUAL_DISCOUNT = 0.20;

// Legacy plan name display (for existing paying customers)
const LEGACY_PLAN_NAMES = {
  crm: 'CRM',
  crm_voice: 'CRM + AI Voice',
  crm_voice_website: 'CRM + Voice + Website',
  crm_voice_website_marketing: 'Full Suite',
  full_suite: 'Full Suite',
  free: 'Free Trial',
};

const LEGACY_PLAN_PRICES = {
  crm: 29,
  crm_voice: 59,
  crm_voice_website: 99,
  crm_voice_website_marketing: 149,
  full_suite: 149,
};

const BillingPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { profile, organization, refreshOrganization } = useAuth();
  const [orgData, setOrgData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [successBanner, setSuccessBanner] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // NEW: modular plan builder state
  const [selectedAddons, setSelectedAddons] = useState({
    ai_voice: false,
    website_builder: false,
    ai_marketing: false,
  });
  const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' | 'annual'
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);
  const [earlyAccessLoading, setEarlyAccessLoading] = useState(false);
  // Allow legacy customers (and the SaaS owner) to preview the new modular pricing.
  // Defaults to true if ?preview=1 in URL, OR if user is a platform admin.
  const [previewMode, setPreviewMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('preview') === '1';
  });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const [features, setFeatures] = useState(null);

  const fetchOrgData = useCallback(async () => {
    if (!profile?.org_id) return;
    const [{ data: orgRow }, { data: featuresRow }] = await Promise.all([
      supabase
        ?.from('organizations')
        ?.select('id, name, plan_type, subscription_status, stripe_customer_id, settings, trial_start_date, trial_end_date, is_legacy_plan')
        ?.eq('id', profile?.org_id)
        ?.single(),
      supabase
        ?.from('org_features')
        ?.select('*')
        ?.eq('org_id', profile?.org_id)
        ?.maybeSingle(),
    ]);
    if (orgRow) setOrgData(orgRow);
    if (featuresRow) setFeatures(featuresRow);
    return orgRow;
  }, [profile?.org_id]);

  // Handle success/cancel URL params from Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params?.get('success') === 'true') {
      window.history?.replaceState({}, '', '/billing');
      setSuccessBanner('🎉 Payment successful! Activating your subscription...');
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const fresh = await fetchOrgData();
        if (fresh && (fresh.subscription_status === 'active' || fresh.subscription_status === 'trialing')) {
          refreshOrganization?.();
          setSuccessBanner('🎉 Payment successful! Your subscription is now active.');
          setTimeout(() => setSuccessBanner(null), 10000);
          return;
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else {
          refreshOrganization?.();
          setSuccessBanner('🎉 Payment received! Your subscription will activate shortly.');
          setTimeout(() => setSuccessBanner(null), 12000);
        }
      };
      setTimeout(poll, 2000);
    } else if (params?.get('canceled') === 'true') {
      showToast('Checkout canceled.', 'info');
      window.history?.replaceState({}, '', '/billing');
    }
  }, [profile?.org_id]);

  useEffect(() => {
    if (!profile?.org_id) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchOrgData();
        const [contactsRes, dealsRes] = await Promise.all([
          supabase?.from('contacts')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id),
          supabase?.from('deals')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id),
        ]);
        setUsageData([
          { name: 'Contacts', icon: 'Users', used: contactsRes?.count || 0, limit: 1000, type: 'count' },
          { name: 'Deals', icon: 'Target', used: dealsRes?.count || 0, limit: 500, type: 'count' },
          { name: 'Storage', icon: 'HardDrive', used: 0.5 * 1024 * 1024 * 1024, limit: 5 * 1024 * 1024 * 1024, type: 'storage' },
          { name: 'API Calls', icon: 'Zap', used: 0, limit: 10000, type: 'count' },
        ]);
      } catch (err) {
        console.error('Billing usage error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    fetchReceipts();
  }, [profile?.org_id]);

  const fetchReceipts = async () => {
    if (!profile?.org_id) return;
    setReceiptsLoading(true);
    try {
      const { data } = await supabase?.from('payment_receipts')?.select('*')?.eq('org_id', profile?.org_id)?.order('created_at', { ascending: false })?.limit(20);
      setReceipts(data || []);
    } catch (err) {
      console.error('Receipts error:', err);
    } finally {
      setReceiptsLoading(false);
    }
  };

  const handleMenuToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  // Toggle an add-on
  const toggleAddon = (addonId) => {
    setSelectedAddons((prev) => ({ ...prev, [addonId]: !prev[addonId] }));
  };

  // ── Live cart calculation ─────────────────────────────────────────────────
  const cart = useMemo(() => {
    const selectedAddonObjs = ADDONS.filter((a) => selectedAddons[a.id]);
    const addonsSubtotal = selectedAddonObjs.reduce((sum, a) => sum + a.price, 0);
    const grossSubtotal = CRM_FOUNDATION.price + addonsSubtotal;
    const bundleDiscountRate = BUNDLE_DISCOUNTS[selectedAddonObjs.length] || 0;
    const bundleDiscountAmount = (CRM_FOUNDATION.price + addonsSubtotal) * bundleDiscountRate;
    const afterBundle = grossSubtotal - bundleDiscountAmount;
    const annualDiscountAmount = billingInterval === 'annual' ? afterBundle * ANNUAL_DISCOUNT : 0;
    const monthlyEffective = afterBundle - annualDiscountAmount;
    return {
      selectedAddonObjs,
      addonsSubtotal,
      grossSubtotal,
      bundleDiscountRate,
      bundleDiscountAmount,
      annualDiscountAmount,
      monthlyEffective,
      annualTotal: monthlyEffective * 12,
    };
  }, [selectedAddons, billingInterval]);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const returnUrl = `${window.location?.origin}/billing`;
      const { data, error } = await supabase?.functions?.invoke('create-portal-session', {
        body: { org_id: profile?.org_id, return_url: returnUrl, app_url: window.location?.origin },
      });
      if (error || !data?.url) {
        showToast(data?.error || error?.message || 'Failed to open billing portal.', 'error');
        return;
      }
      window.location.href = data?.url;
    } catch (err) {
      showToast('Could not open billing portal: ' + (err?.message || 'Unknown error'), 'error');
    } finally {
      setPortalLoading(false);
    }
  };

  // Subscribe — calls create-checkout-session with modular features array.
  // Edge function auto-creates Stripe products/prices on first checkout.
  const handleSubscribe = async () => {
    if (cart.selectedAddonObjs.length === 0 && !confirm('Subscribe to CRM only ($39/mo)?')) return;
    setEarlyAccessLoading(true);
    try {
      const { data: sessionData } = await supabase?.auth?.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        showToast('Not authenticated. Please log in again.', 'error');
        setEarlyAccessLoading(false);
        return;
      }

      // Build features array — always includes 'crm', plus selected add-ons
      const features = ['crm', ...cart.selectedAddonObjs.map((a) => a.id)];

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify({
            features,
            interval: billingInterval,
            org_id: profile?.org_id,
            app_url: window.location?.origin,
            trial_days: 14,
          }),
        }
      );

      const data = await response?.json();

      if (!response?.ok || data?.error) {
        showToast(data?.error || `Checkout failed (${response?.status}).`, 'error');
        setEarlyAccessLoading(false);
        return;
      }

      if (!data?.url) {
        showToast('No checkout URL returned from Stripe.', 'error');
        setEarlyAccessLoading(false);
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = data?.url;
    } catch (err) {
      showToast('Checkout error: ' + (err?.message || 'Unknown error'), 'error');
      setEarlyAccessLoading(false);
    }
  };

  const handlePremierBookCall = () => {
    const subject = encodeURIComponent('Premier inquiry');
    const body = encodeURIComponent(
      `Hi Wilcart team,\n\nI'd like to learn more about Wilcart Premier (white-glove implementation).\n\nOrg ID: ${profile?.org_id}\nEmail: ${profile?.email || ''}\n\nBest time to talk: \n\nThanks!`
    );
    window.location.href = `mailto:support@wilcart.com?subject=${subject}&body=${body}`;
  };

  // ── Render context ────────────────────────────────────────────────────────
  const effectiveOrg = orgData || (loading ? null : organization);
  const isLegacy = !!effectiveOrg?.is_legacy_plan;
  // Platform admins always see the new UI by default (so the SaaS owner can evaluate it).
  const isPlatformAdmin = !!profile?.is_platform_admin;
  const showNewUI = !isLegacy || previewMode || isPlatformAdmin;
  const subscriptionStatus = effectiveOrg?.subscription_status || null;
  const planType = effectiveOrg?.plan_type || null;
  const isActivePlan = (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') && planType && planType !== 'free';
  const isTrialOrFree = !isActivePlan;
  const trialEndDate = effectiveOrg?.trial_end_date;
  const trialDaysRemaining = trialEndDate
    ? Math.max(0, Math.ceil((new Date(trialEndDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    trialing: 'bg-green-100 text-green-700',
    trial: 'bg-amber-100 text-amber-700',
    past_due: 'bg-red-100 text-red-700',
    canceled: 'bg-gray-100 text-gray-600',
  };

  // Detect a modular subscription: legacy=false + active + has at least 1 add-on enabled
  const isModularSub = !isLegacy && isActivePlan && features &&
    (features.has_ai_voice || features.has_website_builder || features.has_ai_marketing);
  const activeFeatureLabels = features ? [
    features.has_crm && { name: 'Wilcart CRM', icon: 'LayoutDashboard', price: 39 },
    features.has_ai_voice && { name: 'AI Voice', icon: 'PhoneCall', price: 79 },
    features.has_website_builder && { name: 'Website Builder', icon: 'Globe', price: 50 },
    features.has_ai_marketing && { name: 'AI Marketing', icon: 'Sparkles', price: 99 },
  ].filter(Boolean) : [];
  const modularMonthlyDisplay = features?.monthly_amount_cents
    ? (features.monthly_amount_cents / 100).toFixed(2)
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Billing & Subscription — Wilcart</title></Helmet>
      <Header onMenuToggle={handleMenuToggle} isSidebarOpen={isSidebarOpen} />
      <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center space-x-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${
          toast?.type === 'success' ? 'bg-green-600 text-white' :
          toast?.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-800 text-white'
        }`}>
          <Icon name={toast?.type === 'success' ? 'CheckCircle' : toast?.type === 'error' ? 'XCircle' : 'Info'} size={18} />
          <span>{toast?.msg}</span>
        </div>
      )}

      <main className="lg:ml-64 pt-24 lg:pt-28">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">Billing & Subscription</h1>
            <p className="text-muted-foreground">
              {isLegacy
                ? 'Manage your existing subscription'
                : 'Build the perfect plan for your business. Pay only for what you need.'}
            </p>
          </div>

          {/* Success Banner */}
          {successBanner && (
            <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-xl flex items-center space-x-3">
              <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
              <p className="text-green-800 font-semibold">{successBanner}</p>
              <button onClick={() => setSuccessBanner(null)} className="ml-auto text-green-600 hover:text-green-800">
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {/* Current Plan Banner */}
          {loading ? (
            <div className="mb-6 p-4 rounded-xl border border-border bg-surface animate-pulse h-20" />
          ) : isModularSub ? (
            // ── Modular subscriber view ──────────────────────────────
            <div className="mb-6 p-5 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Crown" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      Active Subscription · <span className="text-primary">{activeFeatureLabels.length} module{activeFeatureLabels.length !== 1 ? 's' : ''}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ${modularMonthlyDisplay}/mo · Billed {features?.billing_interval === 'annual' ? 'annually' : 'monthly'} · Renews automatically
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Active</span>
                  <button
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 disabled:opacity-60"
                  >
                    {portalLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : <Icon name="Settings" size={13} />}
                    <span>{portalLoading ? 'Opening...' : 'Manage'}</span>
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeFeatureLabels.map((f) => (
                  <div key={f.name} className="flex items-center space-x-1.5 px-2.5 py-1 bg-white border border-primary/20 rounded-full text-xs">
                    <Icon name={f.icon} size={11} className="text-primary" />
                    <span className="font-medium text-foreground">{f.name}</span>
                    <span className="text-muted-foreground">${f.price}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // ── Legacy / trial / free view ────────────────────────────
            <div className={`mb-6 p-4 rounded-xl flex items-center justify-between flex-wrap gap-3 ${
              isTrialOrFree ? 'bg-amber-50 border border-amber-200' : 'bg-primary/5 border border-primary/20'
            }`}>
              <div className="flex items-center space-x-3">
                <Icon name={isTrialOrFree ? 'AlertCircle' : 'Crown'} size={20} className={isTrialOrFree ? 'text-amber-500' : 'text-primary'} />
                <div>
                  <p className="font-semibold text-foreground">
                    Current Plan:{' '}
                    <span className={`font-bold ${isTrialOrFree ? 'text-amber-600' : 'text-primary'}`}>
                      {isTrialOrFree
                        ? 'Free Trial'
                        : (LEGACY_PLAN_NAMES[planType] || planType || 'Active')}
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isTrialOrFree
                      ? (trialDaysRemaining !== null
                          ? (trialDaysRemaining > 0
                              ? `Free Trial — ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} remaining`
                              : 'Free Trial — Expired')
                          : 'Free Trial')
                      : `$${LEGACY_PLAN_PRICES[planType] || 0}/month · Renews automatically`}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${statusColors[subscriptionStatus] || statusColors.trial}`}>
                  {subscriptionStatus === 'active' || subscriptionStatus === 'trialing' ? 'Active'
                    : subscriptionStatus === 'past_due' ? 'Past Due'
                    : subscriptionStatus === 'canceled' ? 'Canceled'
                    : 'Free Trial'}
                </span>
                {isActivePlan && (
                  <button
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 disabled:opacity-60"
                  >
                    {portalLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : <Icon name="Settings" size={13} />}
                    <span>{portalLoading ? 'Opening...' : 'Manage Subscription'}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Past Due Warning */}
          {subscriptionStatus === 'past_due' && (
            <div className="mb-6 p-5 bg-red-50 border border-red-200 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start sm:items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-red-900">Payment overdue</p>
                  <p className="text-sm text-red-700">Update your payment method to keep your subscription active.</p>
                </div>
              </div>
              <button onClick={handleOpenPortal} disabled={portalLoading} className="flex-shrink-0 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 flex items-center space-x-2 disabled:opacity-60">
                <Icon name="CreditCard" size={15} />
                <span>Update Payment Method</span>
              </button>
            </div>
          )}

          {/* Preview-mode banner — shown when legacy customer is viewing new pricing */}
          {isLegacy && showNewUI && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-3">
                <Icon name="Eye" size={18} className="text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-blue-900 text-sm">You're previewing our new modular pricing</p>
                  <p className="text-xs text-blue-700">Your current plan is unchanged. Subscribe below to switch (we'll honor your loyalty pricing — contact support).</p>
                </div>
              </div>
              <button
                onClick={() => setPreviewMode(false)}
                className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100"
              >
                ← Back to my current plan
              </button>
            </div>
          )}

          {/* ============================================================
              LEGACY CUSTOMERS (not in preview mode) — show grandfathered view
              ============================================================ */}
          {isLegacy && !showNewUI ? (
            <div className="mb-8 p-6 bg-card border border-border rounded-xl">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Crown" size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-foreground mb-1">You're on a grandfathered plan</h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    You've been with Wilcart since before we launched modular pricing — your existing price is locked in.
                    To change your plan, payment method, or cancel, use the Stripe portal below.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleOpenPortal}
                      disabled={portalLoading}
                      className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 flex items-center space-x-2 disabled:opacity-60"
                    >
                      {portalLoading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : <Icon name="Settings" size={15} />}
                      <span>{portalLoading ? 'Opening...' : 'Manage Subscription in Stripe'}</span>
                    </button>
                    <button
                      onClick={() => setPreviewMode(true)}
                      className="px-4 py-2.5 border border-border text-foreground rounded-lg text-sm font-medium hover:bg-muted flex items-center space-x-2"
                    >
                      <Icon name="Sparkles" size={15} className="text-amber-500" />
                      <span>Preview new modular pricing →</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* ============================================================
                  NEW MODULAR PRICING — for trial / new customers
                  ============================================================ */}

              {/* Billing interval toggle */}
              <div className="mb-6 flex justify-center">
                <div className="inline-flex bg-muted/50 border border-border rounded-full p-1">
                  <button
                    onClick={() => setBillingInterval('monthly')}
                    className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                      billingInterval === 'monthly'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingInterval('annual')}
                    className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all flex items-center space-x-1.5 ${
                      billingInterval === 'annual'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span>Annual</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      billingInterval === 'annual' ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'
                    }`}>Save 20%</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* LEFT: Foundation + Add-ons (2 cols) */}
                <div className="lg:col-span-2 space-y-4">
                  {/* CRM Foundation card */}
                  <div className="bg-card border-2 border-primary rounded-xl p-5 relative">
                    <div className="absolute -top-3 left-4">
                      <span className="px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-primary text-primary-foreground">
                        🏗 Foundation · Required
                      </span>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${CRM_FOUNDATION.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon name={CRM_FOUNDATION.icon} size={22} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between mb-1">
                          <h3 className="text-lg font-bold text-foreground">{CRM_FOUNDATION.name}</h3>
                          <div>
                            <span className="text-2xl font-bold text-foreground">${CRM_FOUNDATION.price}</span>
                            <span className="text-sm text-muted-foreground">/mo</span>
                          </div>
                        </div>
                        <p className="text-xs text-primary font-medium mb-2">{CRM_FOUNDATION.tagline}</p>
                        <p className="text-sm text-muted-foreground mb-3">{CRM_FOUNDATION.description}</p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {CRM_FOUNDATION.features.map((f, i) => (
                            <li key={i} className="flex items-center space-x-1.5 text-xs">
                              <Icon name="Check" size={13} className="text-green-500 flex-shrink-0" />
                              <span className="text-foreground">{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Add-on cards */}
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3 px-1">
                      ➕ Add what you need
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {ADDONS.map((addon) => {
                        const enabled = selectedAddons[addon.id];
                        return (
                          <button
                            key={addon.id}
                            type="button"
                            onClick={() => toggleAddon(addon.id)}
                            className={`text-left bg-card border-2 rounded-xl p-4 transition-all hover:shadow-md ${
                              enabled ? 'border-primary shadow-md' : 'border-border hover:border-primary/40'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${addon.color} flex items-center justify-center`}>
                                <Icon name={addon.icon} size={18} className="text-white" />
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                enabled ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                              }`}>
                                {enabled && <Icon name="Check" size={12} className="text-primary-foreground" />}
                              </div>
                            </div>
                            <h4 className="font-bold text-foreground text-sm mb-0.5">{addon.name}</h4>
                            <p className="text-xs text-primary mb-1">{addon.tagline}</p>
                            <p className="text-[11px] text-muted-foreground mb-3 leading-snug">{addon.description}</p>
                            <ul className="space-y-1 mb-3">
                              {addon.features.map((f, i) => (
                                <li key={i} className="flex items-center space-x-1 text-[11px]">
                                  <Icon name="Check" size={10} className="text-green-500 flex-shrink-0" />
                                  <span className="text-muted-foreground">{f}</span>
                                </li>
                              ))}
                            </ul>
                            <div className="pt-2 border-t border-border flex items-baseline justify-between">
                              <span className="text-xs text-muted-foreground">add</span>
                              <div>
                                <span className="text-lg font-bold text-foreground">+${addon.price}</span>
                                <span className="text-xs text-muted-foreground">/mo</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* RIGHT: Live calculator (sticky on desktop) */}
                <div className="lg:col-span-1">
                  <div className="bg-card border border-border rounded-xl p-5 sticky top-28">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-4">
                      💰 Your plan
                    </h3>

                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground">Wilcart CRM</span>
                        <span className="text-foreground font-medium">${CRM_FOUNDATION.price}/mo</span>
                      </div>
                      {cart.selectedAddonObjs.map((a) => (
                        <div key={a.id} className="flex justify-between">
                          <span className="text-foreground">+ {a.name}</span>
                          <span className="text-foreground font-medium">${a.price}/mo</span>
                        </div>
                      ))}
                      {cart.selectedAddonObjs.length === 0 && (
                        <p className="text-xs text-muted-foreground italic pt-2">
                          Tap an add-on above to build your plan
                        </p>
                      )}
                    </div>

                    {(cart.bundleDiscountRate > 0 || billingInterval === 'annual') && (
                      <div className="space-y-1.5 mb-3 pt-3 border-t border-border text-xs">
                        <div className="flex justify-between text-muted-foreground">
                          <span>Subtotal</span>
                          <span>${cart.grossSubtotal.toFixed(2)}/mo</span>
                        </div>
                        {cart.bundleDiscountRate > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Bundle save ({cart.selectedAddonObjs.length} add-ons, −{Math.round(cart.bundleDiscountRate * 100)}%)</span>
                            <span>−${cart.bundleDiscountAmount.toFixed(2)}</span>
                          </div>
                        )}
                        {billingInterval === 'annual' && (
                          <div className="flex justify-between text-green-600">
                            <span>Annual save (−20%)</span>
                            <span>−${cart.annualDiscountAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pt-3 border-t border-border mb-4">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">Total</span>
                        <div>
                          <span className="text-3xl font-bold text-foreground">${cart.monthlyEffective.toFixed(2)}</span>
                          <span className="text-sm text-muted-foreground">/mo</span>
                        </div>
                      </div>
                      {billingInterval === 'annual' && (
                        <p className="text-xs text-muted-foreground text-right">
                          Billed annually: ${cart.annualTotal.toFixed(2)}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={handleSubscribe}
                      disabled={earlyAccessLoading}
                      className="w-full py-3 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 flex items-center justify-center space-x-2 mb-2 disabled:opacity-60"
                    >
                      {earlyAccessLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Opening Stripe...</span>
                        </>
                      ) : (
                        <>
                          <Icon name="CreditCard" size={15} />
                          <span>Start 14-day free trial</span>
                        </>
                      )}
                    </button>
                    <p className="text-[11px] text-muted-foreground text-center">
                      No charge for 14 days · Cancel anytime · Secure payment by Stripe
                    </p>
                  </div>
                </div>
              </div>

              {/* ============================================================
                  PREMIER — high-touch tier, no price shown, mailto CTA
                  ============================================================ */}
              <div className="mb-8 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-amber-500/30 rounded-2xl p-8">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -translate-y-32 translate-x-32" />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex-1 max-w-2xl">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                        <Icon name="Award" size={20} className="text-white" />
                      </div>
                      <span className="px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        Wilcart Premier
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      Don't want to set it up yourself?
                    </h2>
                    <p className="text-slate-300 mb-4">
                      Wilcart Premier is our white-glove tier. We do the heavy lifting — implementation, data migration,
                      custom workflows, training — so you focus on running your business.
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                      {[
                        ['Handshake', 'White-glove onboarding & data migration'],
                        ['UserCheck', 'Dedicated success manager'],
                        ['Settings2', 'Custom workflows tailored to you'],
                        ['Zap', 'Priority support (1-hour response)'],
                        ['BarChart3', 'Quarterly strategy reviews'],
                        ['Palette', 'White-label branding option'],
                      ].map(([icon, text], i) => (
                        <li key={i} className="flex items-center space-x-2 text-sm text-slate-200">
                          <Icon name={icon} size={14} className="text-amber-400 flex-shrink-0" />
                          <span>{text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={handlePremierBookCall}
                      className="w-full md:w-auto px-6 py-3.5 bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 rounded-xl text-sm font-bold hover:from-amber-300 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/20 flex items-center justify-center space-x-2"
                    >
                      <Icon name="Calendar" size={16} />
                      <span>Book a 30-min call</span>
                    </button>
                    <p className="text-[11px] text-slate-400 text-center mt-2">
                      Custom pricing · Talk to our team
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Usage Metrics */}
          {usageData && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Usage</h2>
              <UsageMetrics metrics={usageData} />
            </div>
          )}

          {/* Payment History */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Payment History</h2>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              {receiptsLoading ? (
                <div className="p-8 text-center text-muted-foreground">
                  <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-2" />
                  Loading receipts...
                </div>
              ) : receipts?.length === 0 ? (
                <div className="p-8 text-center">
                  <Icon name="Receipt" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">No payment history yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-4 py-3 text-muted-foreground font-medium">Date</th>
                        <th className="text-left px-4 py-3 text-muted-foreground font-medium">Plan</th>
                        <th className="text-left px-4 py-3 text-muted-foreground font-medium">Amount</th>
                        <th className="text-left px-4 py-3 text-muted-foreground font-medium">Promo</th>
                        <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipts?.map((r) => (
                        <tr key={r?.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                          <td className="px-4 py-3 text-foreground">
                            {new Date(r.created_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-foreground capitalize">
                            {LEGACY_PLAN_NAMES[r?.plan_id] || r?.plan_id || '—'}
                          </td>
                          <td className="px-4 py-3 text-foreground font-medium">
                            ${((r?.amount_cents || 0) / 100)?.toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            {r?.promo_code ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">{r?.promo_code}</span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium capitalize">{r?.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default BillingPage;
